import { Button, Classes, Intent } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Dialog } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

interface WithConfirmationOptions {
  dialogClassName?: string;
  isCloseButtonShown?: boolean;
  message: React.ReactNode;
  noButtonIntent?: Intent;
  noButtonOutlined?: boolean;
  noPrompt?: string;
  onConfirmationFailure?: () => any;
  onConfirmationSuccess: () => any;
  title?: string | null;
  yesButtonIntent?: Intent;
  yesButtonOutlined?: boolean;
  yesPrompt?: string;
}

interface WithConfirmationProps extends WithConfirmationOptions {
  noPrompt: string;
  onConfirmationFailure: () => any;
  title: string | null;
  yesPrompt: string;
}

export const WithConfirmation: React.VFC<WithConfirmationProps> = ({
  dialogClassName,
  isCloseButtonShown,
  message,
  noButtonIntent,
  noButtonOutlined,
  noPrompt,
  onConfirmationFailure,
  onConfirmationSuccess,
  title,
  yesButtonIntent,
  yesButtonOutlined,
  yesPrompt,
}) => {
  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-confirmation', dialogClassName)}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isCloseButtonShown={isCloseButtonShown}
      isOpen
      onClose={onConfirmationFailure}
      title={title}
    >
      <form
        onSubmit={(evt) => {
          evt.preventDefault();
          onConfirmationFailure();
        }}
      >
        <div className={Classes.DIALOG_BODY}>
          <div>{message}</div>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              className={Classes.POPOVER_OPEN}
              data-testid={TEST_IDS.dialog.confirmation.yesButton}
              intent={yesButtonIntent}
              onClick={onConfirmationSuccess}
              outlined={yesButtonOutlined}
              text={yesPrompt}
            />
            <Button
              autoFocus
              className={Classes.POPOVER_DISMISS}
              data-testid={TEST_IDS.dialog.confirmation.noButton}
              intent={noButtonIntent}
              outlined={noButtonOutlined}
              text={noPrompt}
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
};

const unmountAndRemove = (node: HTMLElement) => {
  ReactDOM.unmountComponentAtNode(node);
  node.remove();
};

// Render an unattached dialog for receiving confirmation imperatively.
export const withConfirmation = ({
  dialogClassName,
  isCloseButtonShown = true,
  message,
  noButtonIntent = Intent.NONE,
  noButtonOutlined,
  noPrompt = copy.components.defaults.confirmation.no,
  onConfirmationFailure = noop,
  onConfirmationSuccess,
  title = copy.components.defaults.confirmation.title,
  yesButtonIntent = Intent.DANGER,
  yesButtonOutlined,
  yesPrompt = copy.components.defaults.confirmation.yes,
}: WithConfirmationOptions) => {
  const node = document.createElement('div');
  ReactDOM.render(
    <WithConfirmation
      dialogClassName={dialogClassName}
      isCloseButtonShown={isCloseButtonShown}
      message={message}
      noButtonIntent={noButtonIntent}
      noButtonOutlined={noButtonOutlined}
      noPrompt={noPrompt}
      onConfirmationFailure={() => {
        unmountAndRemove(node);
        onConfirmationFailure();
      }}
      onConfirmationSuccess={() => {
        unmountAndRemove(node);
        onConfirmationSuccess();
      }}
      title={title}
      yesButtonIntent={yesButtonIntent}
      yesButtonOutlined={yesButtonOutlined}
      yesPrompt={yesPrompt}
    />,
    node
  );
};
